// ImageLightbox — the one image lightbox of the web app
// (`unify-image-lightbox`). Every surface that shows an image large — composer
// chips, chat history, response assets, story attachments, report images,
// web-test screenshots — renders THIS component over its own set of images
// instead of hand-rolling a `Dialog` around an `<img>`.
//
// It is controlled and never fetches: the caller owns `activeKey` (the `key` of
// the open slide, `null` = closed) and hands over ready-made `slides` whose
// image is already a same-origin `blob:` URL, still `loading`, or
// `unavailable`. That keeps the bytes where they already are (the caller's
// thumbnails and this view read the same blob) and keeps this file free of any
// transport concern.
//
// The open image is tracked by key, never by position: a set that changes under
// an open lightbox (an earlier image turning ready, one appended or removed
// before it) cannot make it jump to a different image, and a key that no longer
// names a slide closes it.
//
// One `Dialog` serves the whole set. Moving between slides only changes its
// content, so the dialog is never unmounted and its focus trap never re-runs:
// keyboard focus stays on the control the reader activated. (The old
// `ResponseAssetGrid` rendered a dialog per card and needed a `navDirection`
// hand-off to put focus back after each step; that workaround dies with it.)
//
// With more than one slide the lightbox is a gallery: previous/next buttons,
// an `n de N` indicator, and `ArrowLeft`/`ArrowRight` on the document, all
// wrapping at the ends. The arrow listener exists only while the lightbox is
// open on such a set, so it never competes with the page for arrow presses;
// arrows pressed with a modifier (`Alt`, `Ctrl`, `Meta`, `Shift` — history
// navigation, text selection, ...) are left to the browser.
// With one slide none of that renders. `Escape` and the close button belong to
// the shared `Dialog`.

import { ChevronLeft, ChevronRight, ImageOff, Loader } from "lucide-react";
import { type JSX, type ReactNode, useCallback, useEffect, useRef } from "react";

import { Dialog } from "@/components/ui/dialog";

export type LightboxImage =
  | { status: "ready"; url: string }
  | { status: "loading" }
  | { status: "unavailable" };

export interface LightboxSlide {
  key: string;
  /** `alt` of the image and the dialog's accessible name (`Imagen: <alt>`). */
  alt: string;
  /** Optional visible line above the image (a file name). */
  heading?: string;
  image: LightboxImage;
  /** Optional content under the image: caption, locator, actions. */
  details?: ReactNode;
}

interface ImageLightboxProps {
  slides: readonly LightboxSlide[];
  /** `key` of the open slide; `null` means closed. */
  activeKey: string | null;
  onActiveKeyChange: (key: string) => void;
  onClose: () => void;
}

const NAV_BUTTON_CLASS =
  "shrink-0 rounded-md border border-border p-2 text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

// The body mirrors the thumbnail's three states, so navigating onto a sibling
// that is still loading — or one that turned out to be unavailable — keeps the
// dialog open on that slide instead of closing it or skipping past it.
function renderImage(slide: LightboxSlide): JSX.Element {
  const { image } = slide;
  if (image.status === "unavailable") {
    return (
      <div
        className="flex min-h-40 flex-col items-center justify-center gap-2 self-center text-center text-body-3 text-muted-foreground"
        data-testid="image-lightbox-unavailable"
      >
        <ImageOff size={28} aria-hidden />
        <span>No disponible</span>
      </div>
    );
  }
  if (image.status === "loading") {
    return (
      <div
        className="flex min-h-40 items-center justify-center self-center"
        data-testid="image-lightbox-loading"
      >
        <Loader size={20} className="animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }
  return (
    <img
      src={image.url}
      alt={slide.alt}
      className="max-h-[70vh] max-w-full self-center rounded object-contain"
    />
  );
}

export function ImageLightbox({
  slides,
  activeKey,
  onActiveKeyChange,
  onClose,
}: ImageLightboxProps): JSX.Element | null {
  const total = slides.length;
  const index = activeKey === null ? -1 : slides.findIndex((s) => s.key === activeKey);
  const slide = slides[index];
  const isOpen = slide !== undefined;
  const missing = activeKey !== null && !isOpen;
  const navigable = total > 1;

  // The active image can vanish from under an open lightbox (a removed
  // attachment, a set that shrank): close instead of rendering nothing inside
  // an open dialog. `onClose` rides in a ref because callers hand a new
  // function every render and this must fire once per episode, not per render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (missing) onCloseRef.current();
  }, [missing]);

  const step = useCallback(
    (delta: -1 | 1): void => {
      const target = slides[(index + delta + total) % total];
      if (index < 0 || target === undefined) return;
      onActiveKeyChange(target.key);
    },
    [slides, index, total, onActiveKeyChange],
  );

  // Arrow keys are the keyboard equivalent of the on-screen controls.
  useEffect(() => {
    if (!isOpen || !navigable) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      event.preventDefault();
      step(event.key === "ArrowRight" ? 1 : -1);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, navigable, step]);

  if (slide === undefined) return null;

  return (
    <Dialog open onClose={onClose} ariaLabel={`Imagen: ${slide.alt}`}>
      <div className="flex flex-col gap-3 p-6" data-testid="image-lightbox">
        {/* Always rendered, even with no heading: the Dialog's close button is
            absolutely positioned at the top right and this row keeps the image
            below it. */}
        <div
          className="min-h-6 pr-8 text-body-3 text-muted-foreground [overflow-wrap:anywhere]"
          data-testid="image-lightbox-header"
        >
          {slide.heading !== undefined ? <p>{slide.heading}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          {navigable ? (
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Imagen anterior"
              className={NAV_BUTTON_CLASS}
            >
              <ChevronLeft size={18} aria-hidden />
            </button>
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col">{renderImage(slide)}</div>
          {navigable ? (
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Imagen siguiente"
              className={NAV_BUTTON_CLASS}
            >
              <ChevronRight size={18} aria-hidden />
            </button>
          ) : null}
        </div>
        {navigable ? (
          <p
            className="text-label-2 text-muted-foreground"
            aria-live="polite"
            data-testid="image-lightbox-position"
          >
            {index + 1} de {total}
          </p>
        ) : null}
        {slide.details}
      </div>
    </Dialog>
  );
}
